// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSlice } from '@reduxjs/toolkit';

import { addAsyncCases, updateStateStatus } from '../utils/utils';

import {
  fetchShowScripts,
  createScriptLine,
  updateScriptLine,
  deleteScriptLine,
  createScriptSubjects,
  updateScriptSubjects,
  deleteScriptSubjects,
  createScriptLocations,
  updateScriptLocations,
  deleteScriptLocations,
} from './scriptsActions';

import {
  updateScriptLineState,
  addScriptLineState,
  deleteScriptLineState,
  updateScriptSubjectState,
  addScriptSubjectState,
  deleteScriptSubjectState,
  addScriptLocationState,
  updateScriptLocationState,
  deleteScriptLocationState,
} from './scriptsReducers';

const initialState = {
  scripts: {
    lines: [],
    subjects: {},
    locations: {},
  },
  clipboardLine: null,
  status: { scripts: 'idle' }, // possible status state : 'idle' | 'loading' | 'succeeded' | 'failed'
  error: { scripts: null },
};

const scriptsSlice = createSlice({
  name: 'scripts',
  initialState,
  reducers: {
    clearError: state => {
      state.error = initialState.error;
    },
    clearScripts: state => {
      state.status.scripts = 'idle';
      state.error.scripts = null;
    },
    copyScriptLine: (state, action) => {
      state.clipboardLine = action.payload;
    },
    clearClipboard: state => {
      state.clipboardLine = null;
    },
    wsCreateScriptLine: (state, action) => addScriptLineState(state, action.payload),
    wsUpdateScriptLine: (state, action) => updateScriptLineState(state, action.payload),
    wsDeleteScriptLine: (state, action) => deleteScriptLineState(state, action.payload),
    wsCreateScriptSubject: (state, action) => addScriptSubjectState(state, action.payload),
    wsUpdateScriptSubject: (state, action) => updateScriptSubjectState(state, action.payload),
    wsDeleteScriptSubject: (state, action) => deleteScriptSubjectState(state, action.payload),
    wsCreateScriptLocation: (state, action) => addScriptLocationState(state, action.payload),
    wsUpdateScriptLocation: (state, action) => updateScriptLocationState(state, action.payload),
    wsDeleteScriptLocation: (state, action) => deleteScriptLocationState(state, action.payload),
  },
  extraReducers: builder => {
    addAsyncCases(builder, [
      { action: fetchShowScripts, key: 'scripts' },
      { action: updateScriptLine, key: 'scripts', updateData: false },
      { action: updateScriptSubjects, key: 'scripts', updateData: false },
      { action: updateScriptLocations, key: 'scripts', updateData: false },
    ]);
    builder
      // TODO: once backend has implemented websocket event for create/delete script line/subject/location, we can remove the extraReducers cases for create/delete and rely solely on the websocket reducers above and add them to the list in addAsyncCases to manage error states only
      // Script lines
      // create script line
      .addCase(createScriptLine.fulfilled, (state, action) => {
        addScriptLineState(state, action.payload);
        updateStateStatus(state, 'scripts', 'succeeded');
      })
      .addCase(createScriptLine.rejected, (state, action) => {
        updateStateStatus(state, 'scripts', 'failed', action);
      })
      // delete script line
      .addCase(deleteScriptLine.fulfilled, (state, action) => {
        deleteScriptLineState(state, action.payload);
        updateStateStatus(state, 'scripts', 'succeeded');
      })
      .addCase(deleteScriptLine.rejected, (state, action) => {
        updateStateStatus(state, 'scripts', 'failed', action);
      })
      // Script subjects
      // create script subjects
      .addCase(createScriptSubjects.fulfilled, (state, action) => {
        addScriptSubjectState(state, action.payload);
        updateStateStatus(state, 'scripts', 'succeeded');
      })
      .addCase(createScriptSubjects.rejected, (state, action) => {
        updateStateStatus(state, 'scripts', 'failed', action);
      })
      // delete script subjects
      .addCase(deleteScriptSubjects.fulfilled, (state, action) => {
        const subjectUuid = action.payload;
        deleteScriptSubjectState(state, subjectUuid);
        updateStateStatus(state, 'scripts', 'succeeded');
      })
      .addCase(deleteScriptSubjects.rejected, (state, action) => {
        let message = 'Failed to delete subject.';

        if (action.payload?.includes('violates foreign key constraint')) {
          const subjectUuid = action.meta.arg;
          const subject = state.scripts.subjects[subjectUuid];
          const subjectName = subject?.name || 'This subject';
          message = `${subjectName}  is still used in script lines. Please update or delete those lines first.`;
        }
        updateStateStatus(state, 'scripts', 'failed', { payload: message });
      })
      // Script locations
      // create script locations
      .addCase(createScriptLocations.fulfilled, (state, action) => {
        addScriptLocationState(state, action.payload);
        updateStateStatus(state, 'scripts', 'succeeded');
      })
      .addCase(createScriptLocations.rejected, (state, action) => {
        updateStateStatus(state, 'scripts', 'failed', action);
      })
      // delete script locations
      .addCase(deleteScriptLocations.fulfilled, (state, action) => {
        const locationUuid = action.payload;
        deleteScriptLocationState(state, locationUuid);
        updateStateStatus(state, 'scripts', 'succeeded');
      })
      .addCase(deleteScriptLocations.rejected, (state, action) => {
        updateStateStatus(state, 'scripts', 'failed', action);
      });
  },
});
export const {
  clearError,
  clearScripts,
  copyScriptLine,
  clearClipboard,
  wsCreateScriptLine,
  wsUpdateScriptLine,
  wsDeleteScriptLine,
  wsCreateScriptSubject,
  wsUpdateScriptSubject,
  wsDeleteScriptSubject,
  wsCreateScriptLocation,
  wsUpdateScriptLocation,
  wsDeleteScriptLocation,
} = scriptsSlice.actions;

export const scriptsStatus = state => state.scripts.status;
export const scriptsError = state => state.scripts.error;

export default scriptsSlice.reducer;
